import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../../constants/style/common.styled';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import Button from '../../../components/button';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetGrantAcceptanceTemplateList,
  usePutGrantAcceptanceTemplateEditableTable,
  useWealthlaneAwardApi,
} from '../../../hooks';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import GrantAcceptanceTemplateModal from './grantAcceptanceTemplateModal';
import { ModalStatusType } from '../../../types/common';
import InitialValuesForm from '../../../constants/initialValues';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import {
  GrantAcceptanceTemplateService,
  NoticeService,
} from '../../../services/wealthlane-award-services';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import AlertPopup from '../../../components/alert-popup';
import { axiosInstance } from '../../../services/apiInterceptor';
import EditTemplateNameStatus from './editTemplateNameStatus';
import { TableGrantAcceptanceModifier } from './grantTemplate.styled';
import { GrantAcceptanceTemplateHeader } from '../../../constants/table-headers/grantAcceptanceTemplateHeader';
import { ActionButton } from '../../../components/vertical-dot/popper';
import { EditedRow } from '../../../packages/table-builder/components/Table';

export const grantAcceptanceTemplateInitialValues = {
  data: InitialValuesForm.grantAcceptanceTemplate,
};

// Hardcoded schema for Grant Acceptance Template table with sub-rows
const schemaData = {
  columns: [
    {
      Header: 'Name',
      accessor: 'templateName',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
    },
    {
      Header: 'Status',
      accessor: 'statusName',
      width: 200,
      isBadgeField: true,
      // cellCenter: true,
      // center: true,
      badgeStatus: {
        Active: 'success',
        InActive: 'warning',
        Draft: 'info',
      },
    },
    {
      Header: 'No of Versions',
      accessor: 'noOfVersions',
      sortable: false,
    },
  ],
  subRowColumns: [
    {
      Header: 'Version',
      accessor: 'version',
      width: 200,
      // cellCenter: true,
      // center: true,
    },
    {
      Header: 'Effective Date',
      accessor: 'effectiveDate',
      isDateField: true,
    },
  ],
  options: {
    showToolbar: true,
    showPagination: true,
    showFilter: false,
    showSearch: false,
    subRowKey: 'versions',
  },
};

interface VersionItem {
  contentId?: string;
  version?: string | null;
  effectiveDate?: string | null;
}

interface TemplateListItem {
  templateId?: string;
  templateName?: string | null;
  statusId?: number;
  statusName?: string | null;
  versions?: VersionItem[];
}

interface TransformedTemplate {
  id: string;
  templateId: string;
  templateName: string;
  statusId: number;
  statusName: string;
  noOfVersions: number;
  versions: Array<{
    id: string;
    contentId: string;
    version: string;
    effectiveDate: string | null;
    actions?: React.ReactNode;
  }>;
}

// Function to transform API data to add noOfVersions and id fields
const transformTemplateData = (items: TemplateListItem[] | undefined): TransformedTemplate[] => {
  if (!items || items.length === 0) return [];

  return items.map((item) => ({
    id: item.templateId ?? '',
    templateId: item.templateId ?? '',
    templateName: item.templateName ?? '',
    statusId: item.statusId ?? 0,
    statusName: item.statusName ?? '',
    noOfVersions: item.versions?.length ?? 0,
    versions: (item.versions ?? []).map((v) => ({
      id: v.contentId ?? '',
      contentId: v.contentId ?? '',
      version: v.version ?? '',
      effectiveDate: v.effectiveDate ?? null,
    })),
  }));
};

const GrantAcceptanceTemplate = () => {
  const { handleRequest } = useWealthlaneAwardApi();
  const { errors, setErrors, tableInfo } = useDynamicTable();
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [initialData, setInitialData] = useState(InitialValuesForm.grantAcceptanceTemplate);
  const [templateNameAndStatusData, setTemplateNameAndStatusData] = useState(null);
  const [deleteContentId, setDeleteContentId] = useState<boolean>(false);
  const [saveErrors, setSaveErrors] = useState<boolean>(false);

  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isEditTemplateModalOpen,
    setFalse: closeEditTemplateModal,
    setTrue: openEditTemplateModal,
  } = useBoolean(false);

  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const {
    value: isActivateModalOpen,
    setFalse: closeActivateModal,
    setTrue: openActivateModal,
    customValue: activateCustomValue,
    setCustomValue: setActivateCustomValue,
  } = useAdvanceBoolean<any>(false);
  const {
    value: isDeactivateModalOpen,
    setFalse: closeDeactivateModal,
    setTrue: openDeactivateModal,
    customValue: deactivateCustomValue,
    setCustomValue: setDeactivateCustomValue,
  } = useAdvanceBoolean<any>(false);
  const queryClient = useQueryClient();
  const [modalLoading, setModalLoading] = useState(false);
  const {
    value: isSaveOpen,
    setFalse: closeSaveModal,
    setTrue: openSaveModal,
    customValue: saveCustomValue,
    setCustomValue: setSaveCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [resolveSave, setResolveSave] = useState<(() => void) | null>(null);

  const {
    data: list,
    status,
    isFetching,
    refetch: refetchGrantAcceptanceTemplateList,
  } = useGetGrantAcceptanceTemplateList(handleRequest, tableInfo);

  const {
    mutateAsync,
    isLoading: isGrantAcceptanceTemplateLoading,
    status: isGrantAcceptanceTemplateStatus,
  } = usePutGrantAcceptanceTemplateEditableTable(
    handleRequest,
    closeSaveModal,
    (message, newErrors) => {
      setErrors([...errors, ...newErrors]);
    },
    setSaveErrors
  );

  const getGrantAcceptanceTemplateNameAndStatus = (id: string) => {
    handleRequest(
      GrantAcceptanceTemplateService.getApiAwardApiAppGrantAcceptanceTemplateTemplate(id)
    )
      .then((data: any) => {
        setTemplateNameAndStatusData(data);
        openEditTemplateModal();
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };
  const getGrantAcceptanceTemplateContent = (id: string) => {
    handleRequest(
      GrantAcceptanceTemplateService.getApiAwardApiAppGrantAcceptanceTemplateContent(id)
    )
      .then((data: any) => {
        setInitialData(data);
        openModal();
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'GRANT_ACCEPTANCE_TEMPLATE_TABLE_LIST',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  // Transform API data to add noOfVersions and id fields
  const tableData = useMemo(() => {
    const data = transformTemplateData(list?.items);
    return data.map((item) => ({
      ...item,
      versions: item.versions?.map((v) => ({
        ...v,
        actions: (
          <ActionButton
            actions={[
              {
                action: 'Preview',
                onClick: (id: string) => {
                  window.sessionStorage.setItem('grantAcceptanceTemplatePreviewContentId', id);
                  window.sessionStorage.removeItem('grantAcceptanceTemplatePreviewContent');
                  window.open(
                    `/settings/organization-settings/grant-acceptance-template/preview${window.location.hash}`,
                    '_blank'
                  );
                },
              },
              {
                action: 'Edit Content',
                as: 'Edit',
                onClick: (id: string, rowData: any) => {
                  setModalStatus('Edit');
                  // getGrantAcceptanceTemplate(id);
                  getGrantAcceptanceTemplateContent(id);
                  openModal();
                },
              },
              {
                action: 'Copy Content',
                // as: 'Edit',
                onClick: (id: string, rowData: any) => {
                  setModalStatus('Clone');
                  // getGrantAcceptanceTemplate(id);
                  getGrantAcceptanceTemplateContent(id);
                  openModal();
                },
              },
              {
                action: <div className='delete-label'>Delete Version</div>,
                as: 'Delete',
                onClick: (id: string) => {
                  setDeleteContentId(true);
                  setModalStatus('Delete');
                  setCustomValue(id);
                  openConfirmModal();
                },
              },
            ]}
            id={v.contentId}
            rowData={v as Record<string, any>}
            hideMenuOnActionClick
          />
        ),
      })),
    }));
  }, [list?.items]);

  const tableActions = [
    // {
    //   id: 'view',
    //   action: 'View',
    //   onClick: (id: string) => {
    //     setModalStatus('View');
    //     openModal();
    //   },
    // },
    // {
    //   id: 'view',
    //   action: 'Activate',
    //   onClick: (id: string, rowData: any) => {
    //     setActivateCustomValue(rowData);
    //     openActivateModal();
    //   },
    // },
    // {
    //   id: 'deactivate',
    //   action: 'Inactive',
    //   onClick: (id: string, rowData: any) => {
    //     setDeactivateCustomValue(rowData);
    //     openDeactivateModal();
    //   },
    // },
    // {
    //   action: 'Edit Template',
    //   // as: 'Edit',
    //   onClick: (id: string, rowData: any) => {
    //     setModalStatus('Edit');
    //     getGrantAcceptanceTemplateNameAndStatus(id);
    //     // openEditTemplateModal();
    //   },
    // },
    {
      action: 'Add Version',
      // as: 'Edit',
      onClick: (id: string, rowData: any) => {
        setModalStatus('Add Versions');
        // getGrantAcceptanceTemplate(id);
        getGrantAcceptanceTemplateContent(rowData.versions[0].contentId);
        openModal();
      },
    },
    {
      id: 'delete',
      action: <div className='delete-label'>Delete Template</div>,
      as: 'Delete',
      onClick: (id: string, rowData: any) => {
        setModalStatus('Delete');
        setCustomValue(rowData.templateId);
        openConfirmModal();
      },
    },
  ];

  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteGrantAcceptanceTemplate(customValue);
  };

  const deleteGrantAcceptanceTemplate = (id: string): void => {
    setModalLoading(true);
    if (deleteContentId) {
      handleRequest(
        GrantAcceptanceTemplateService.deleteApiAwardApiAppGrantAcceptanceTemplateContent(id)
      )
        .then((data: any) => {
          if (data != null) {
            toast.success(
              <ToastComponent label={'Success'} message={'Grant Acceptance Template deleted.'} />
            );
            void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
            setModalLoading(false);
            closeConfirmModal();
            setDeleteContentId(false);
          }
        })
        .catch(() => {
          setModalLoading(false);
          setDeleteContentId(false);
          closeConfirmModal();
        })
        .finally(() => {
          setModalLoading(false);
          void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
          closeConfirmModal();
        });
    } else {
      handleRequest(
        GrantAcceptanceTemplateService.deleteApiAwardApiAppGrantAcceptanceTemplateTemplate(id)
      )
        .then((data: any) => {
          if (data != null) {
            toast.success(
              <ToastComponent label={'Success'} message={'Grant Acceptance Template deleted.'} />
            );
            void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
            setModalLoading(false);
            closeConfirmModal();
          }
        })
        .catch(() => {
          setModalLoading(false);
          closeConfirmModal();
        })
        .finally(() => {
          setModalLoading(false);
          void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
          closeConfirmModal();
        });
    }
  };
  const onActivateClick = (values: Record<string, any>): void => {
    activateGrantAcceptanceTemplate(activateCustomValue);
  };

  const activateGrantAcceptanceTemplate = (values: any): void => {
    const payload = {
      id: values.id,
      templateName: values.templateName,
      statusId: 2,
    };
    setModalLoading(true);
    handleRequest(
      GrantAcceptanceTemplateService.putApiAwardApiAppGrantAcceptanceTemplateTemplate(payload)
    )
      .then((data: any) => {
        if (data != null) {
          toast.success(
            <ToastComponent label={'Success'} message={'Grant Acceptance Template Activated.'} />
          );
          void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
          setModalLoading(false);
          closeActivateModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeActivateModal();
      })
      .finally(() => {
        setModalLoading(false);
        void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
        closeActivateModal();
      });
  };
  const onDeactivateClick = (values: Record<string, any>): void => {
    deactivateGrantAcceptanceTemplate(deactivateCustomValue);
  };

  const deactivateGrantAcceptanceTemplate = (values: any): void => {
    const payload = {
      id: values.id,
      templateName: values.templateName,
      statusId: 3,
    };
    setModalLoading(true);
    handleRequest(
      GrantAcceptanceTemplateService.putApiAwardApiAppGrantAcceptanceTemplateTemplate(payload)
    )
      .then((data: any) => {
        if (data != null) {
          toast.success(
            <ToastComponent label={'Success'} message={'Grant Acceptance Template Inactivated.'} />
          );
          void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
          setModalLoading(false);
          closeDeactivateModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeDeactivateModal();
      })
      .finally(() => {
        setModalLoading(false);
        void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
        closeDeactivateModal();
      });
  };
  const grantAcceptanceTemplatePayload = (editedData: any[]) => {
    return {
      templates: editedData.map((item) => ({
        templateId: item.templateId,
        name: item.templateName,
        status: item.statusName.includes('Active')
          ? 2
          : item.statusName.includes('Inactive')
          ? 3
          : item.statusId,
      })),
      contents: editedData.flatMap((item) =>
        (item.versions ?? []).map((version: any) => ({
          contentId: version.contentId,
          effectiveDate: version.undefined ? version.undefined : version.effectiveDate,
        }))
      ),
    };
  };
  const handleSave = async (editedData: EditedRow[] | EditedRow) => {
    if (Array.isArray(editedData)) {
      if (editedData.length === 1) {
        setSaveCustomValue(grantAcceptanceTemplatePayload([editedData?.[0] as any]));
      } else {
        setSaveCustomValue(grantAcceptanceTemplatePayload(editedData as any));
      }
    } else {
      setSaveCustomValue(grantAcceptanceTemplatePayload([editedData as any]));
    }
    openSaveModal();
    await new Promise<void>((resolve) => {
      setResolveSave(() => resolve);
    });
  };

  const saveGrantAcceptanceTemplate = async () => {
    setSaveErrors(false);
    try {
      const payload = saveCustomValue;
      await mutateAsync(payload as any);
      resolveSave && resolveSave();
      refetchGrantAcceptanceTemplateList();
    } catch (error) {
    } finally {
      setSaveCustomValue(null);
      closeSaveModal();
    }
  };

  const renderRightToolbarButton = () => {
    return (
      <Button
        variant={'primary'}
        as='Create'
        title={'Add Template'}
        icon={'add_circle_outline'}
        id='addGrant'
        onClick={() => {
          openModal();
          setModalStatus('Add');
          setInitialData(InitialValuesForm.grantAcceptanceTemplate);
        }}
      />
    );
  };

  const schema = useMemo(
    () => ({
      columns: GrantAcceptanceTemplateHeader({
        actions: schemaData?.columns ?? [],
        tableActions,
      }),
      subRowColumns: [
        ...(schemaData?.subRowColumns ?? []),
        {
          Header: '',
          accessor: 'actions',
          width: 80,
        },
      ],
      options: schemaData?.options ?? {},
      apiInjection: schemaData?.apiInjection as any,
      subRowApiInjection: schemaData?.subRowApiInjection as any,
    }),
    [tableActions, schemaData]
  );
  return (
    <>
      <PageHeader className='flex items-end justify-between'>
        <div>
          <Heading variant='h3' title='Grant Acceptance Templates' />
          <Heading
            variant='body-s'
            title={`Grant Acceptance template list.`}
            color={`${COLORS.Gray}`}
          />
        </div>
      </PageHeader>
      {isConfirmOpen && (
        <AlertPopup
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this Template'}
          handleSubmit={onPrimaryClick}
          loading={modalLoading}
        />
      )}
      {isActivateModalOpen && (
        <AlertPopup
          isShow={isActivateModalOpen}
          closeModal={closeActivateModal}
          modalStatus={modalStatus}
          customTitle={'Are you sure you want to activate this Template?'}
          handleSubmit={onActivateClick}
          loading={modalLoading}
        />
      )}
      {isDeactivateModalOpen && (
        <AlertPopup
          isShow={isDeactivateModalOpen}
          closeModal={closeDeactivateModal}
          modalStatus={modalStatus}
          customTitle={'Are you sure you want to inactivate this Template?'}
          handleSubmit={onDeactivateClick}
          loading={modalLoading}
        />
      )}
      <TableGrantAcceptanceModifier>
        <DynamicTable
          id='dataTable'
          schema={schema}
          data={tableData}
          totalCounts={list?.totalCount}
          loading={isFetching ? 'loading' : status}
          onSave={handleSave}
          isSaveLoading={false}
          components={{
            renderRightToolbarButton,
          }}
        />
      </TableGrantAcceptanceModifier>
      <AnimationWrapper>
        {isModalOpen && (
          <GrantAcceptanceTemplateModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
            initialData={initialData}
            loading={false}
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper>
        {isEditTemplateModalOpen && (
          <EditTemplateNameStatus
            isModalOpen={isEditTemplateModalOpen}
            closeModal={closeEditTemplateModal}
            initialData={templateNameAndStatusData}
            loading={false}
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper>
        {isSaveOpen && (
          <AlertPopup
            id='saveConfirmationModal'
            isShow={isSaveOpen}
            closeModal={closeSaveModal}
            customTitle={
              <div id='modalMessage' className='text-center w-full'>
                Are you sure you want to Save?
              </div>
            }
            handleSubmit={saveGrantAcceptanceTemplate}
            loading={saveErrors === true ? false : isGrantAcceptanceTemplateLoading}
          />
        )}
      </AnimationWrapper>
    </>
  );
};

export default withDynamicTableContext(GrantAcceptanceTemplate, false, axiosInstance);
